import React from 'react';
import { 
  Box, 
  Typography, 
  Stack, 
  FormControlLabel, 
  Switch,
  MenuItem,
  Alert,
  Button,
  IconButton
} from '@mui/material';
import { 
  Home as FeaturesIcon,
  LocalParking as ParkingIcon,
  LocalLaundryService as LaundryIcon,
  Kitchen as AppliancesIcon,
  Thermostat as HvacIcon,
  VpnKey as AccessIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useFormContext, Controller, useFieldArray } from 'react-hook-form';
import FormTextField from '../../shared/FormTextField';
import FormSelect from '../../shared/FormSelect';
import FormCheckboxGroup from '../../shared/FormCheckboxGroup';
import StepFormLayout from '../../shared/StepFormLayout';
import FormSection from '../../shared/FormSection';

interface PropertyFeaturesStepProps {
  stepProgress: number;
}

const PropertyFeaturesStep: React.FC<PropertyFeaturesStepProps> = ({ stepProgress }) => {
  const { control, watch } = useFormContext();
  const parkingIncluded = watch('features.parking.included');
  const furnished = watch('features.furnished');
  const keyTypes = useFieldArray({
    control,
    name: 'features.access.keyTypes',
  });

  const applianceOptions = [
    { value: 'refrigerator', label: 'Refrigerator' },
    { value: 'stove', label: 'Stove/Oven' },
    { value: 'dishwasher', label: 'Dishwasher' },
    { value: 'microwave', label: 'Microwave' },
    { value: 'washer', label: 'Washer' },
    { value: 'dryer', label: 'Dryer' },
    { value: 'garbage_disposal', label: 'Garbage Disposal' },
  ];

  const parkingTypes = [
    { value: 'street', label: 'Street Parking' },
    { value: 'driveway', label: 'Driveway' },
    { value: 'garage', label: 'Garage' },
    { value: 'covered', label: 'Covered Parking' },
    { value: 'assigned', label: 'Assigned Space' },
  ];

  return (
    <StepFormLayout
      title="Property Features & Amenities"
      description="Configure property features and amenities"
      icon={<FeaturesIcon sx={{ fontSize: 28, color: 'primary.main' }} />}
      progress={stepProgress}
      showOptionalBadge={true}
    >
      <Box>
      
      {/* Quick tip */}
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          <strong>🏠 Tip:</strong> Features and amenities can help differentiate your property and justify rental rates.
        </Typography>
      </Alert>
      
      {/* Parking */}
      <FormSection
        title="Parking"
        icon={<ParkingIcon color="primary" />}
        tooltip="Parking availability and details"
        isRequired={false}
        defaultExpanded={false}
      >
        <Stack spacing={3}>
          <Controller
            name="features.parking.included"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} />}
                label="Parking Included"
              />
            )}
          />
          
          {parkingIncluded && (
            <>
              <FormTextField
                name="features.parking.spaces"
                control={control}
                label="Number of Parking Spaces"
                type="number"
              />
              <FormCheckboxGroup
                name="features.parking.types"
                control={control}
                label="Parking Type(s)"
                options={parkingTypes}
              />
            </>
          )}
          <FormTextField
            name="features.parking.rules"
            control={control}
            label="Parking Rules / Location Notes"
            placeholder="e.g., Street parking only on Vista Chino; display permit in windshield"
            multiline
            rows={2}
          />
        </Stack>
      </FormSection>
        
        {/* Laundry */}
        <FormSection
          title="Laundry"
          icon={<LaundryIcon color="primary" />}
        >
          <FormSelect
            name="features.laundry.type"
            control={control}
            label="Laundry Facilities"
            required
          >
            <MenuItem value="in_unit">In Unit</MenuItem>
            <MenuItem value="on_site">On Site (Shared)</MenuItem>
            <MenuItem value="none">None</MenuItem>
          </FormSelect>
        </FormSection>
        
        {/* Appliances */}
        <FormSection
          title="Included Appliances"
          icon={<AppliancesIcon color="primary" />}
          tooltip="Select all appliances included with the rental"
        >
          <Stack spacing={3}>
            <FormCheckboxGroup
              name="features.appliances.included"
              control={control}
              options={applianceOptions}
            />
            
            {/* Furnished */}
            <Controller
              name="features.furnished"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value} />}
                  label="Property is Furnished"
                />
              )}
            />

            {furnished && (
              <FormTextField
                name="features.furnishings"
                control={control}
                label="Furnishings Details"
                multiline
                rows={3}
              />
            )}
          </Stack>
        </FormSection>

        {/* HVAC */}
        <FormSection
          title="HVAC System"
          icon={<HvacIcon color="primary" />}
          tooltip="Heating and cooling system information"
        >
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <FormSelect
              name="features.hvac.heating"
              control={control}
              label="Heating Type"
              required
            >
              <MenuItem value="Central (Forced Air)">Central (Forced Air)</MenuItem>
              <MenuItem value="Radiant">Radiant</MenuItem>
              <MenuItem value="Heat Pump">Heat Pump</MenuItem>
              <MenuItem value="Electric Baseboard">Electric Baseboard</MenuItem>
              <MenuItem value="Wall Heater">Wall Heater</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
              <MenuItem value="None">None</MenuItem>
            </FormSelect>
            <FormSelect
              name="features.hvac.cooling"
              control={control}
              label="Cooling Type"
              required
            >
              <MenuItem value="Central A/C">Central A/C</MenuItem>
              <MenuItem value="Mini-split">Mini-split</MenuItem>
              <MenuItem value="Window Unit(s)">Window Unit(s)</MenuItem>
              <MenuItem value="Evaporative (Swamp Cooler)">Evaporative (Swamp Cooler)</MenuItem>
              <MenuItem value="Ceiling Fans Only">Ceiling Fans Only</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
              <MenuItem value="None">None</MenuItem>
            </FormSelect>
          </Box>
        </FormSection>

        {/* Access */}
        <FormSection
          title="Property Access"
          icon={<AccessIcon color="primary" />}
        >
          <Stack spacing={2}>
            <FormTextField
              name="features.access.keyCount"
              control={control}
              label="Total Key Sets Provided"
              type="number"
              required
            />
            <Stack spacing={2}>
              <Typography variant="subtitle2">Key Types & Counts</Typography>
              {keyTypes.fields.map((field, index) => (
                <Box
                  key={field.id}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    alignItems: { xs: 'flex-start', sm: 'center' },
                  }}
                >
                  <FormTextField
                    name={`features.access.keyTypes.${index}.type`}
                    control={control}
                    label="Key Type"
                    placeholder="e.g., Unit door, Mailbox"
                  />
                  <FormTextField
                    name={`features.access.keyTypes.${index}.count`}
                    control={control}
                    label="Count"
                    type="number"
                  />
                  <IconButton
                    aria-label="Remove key type"
                    onClick={() => keyTypes.remove(index)}
                    sx={{ mt: { xs: 0, sm: 0.5 } }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => keyTypes.append({ type: '', count: 1 })}
                size="small"
                sx={{ alignSelf: 'flex-start' }}
              >
                Add Key Type
              </Button>
            </Stack>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <FormTextField
                name="features.access.garageRemotes"
                control={control}
                label="Garage Remotes"
                type="number"
              />
              <FormTextField
                name="features.access.gateFobs"
                control={control}
                label="Gate Fobs"
                type="number"
              />
            </Box>
            <FormTextField
              name="features.access.accessCodes"
              control={control}
              label="Access Codes / Keypads"
              placeholder="Gate code, smart lock, alarm panel codes"
              multiline
              rows={2}
            />
            <FormTextField
              name="features.access.otherAccessDevices"
              control={control}
              label="Other Access Devices"
              placeholder="e.g., Pool cards, mailbox keys, elevator fobs"
            />
            <FormTextField
              name="features.access.keyReplacementFee"
              control={control}
              label="Key Replacement Fee (per unreturned key)"
              type="number"
            />
            <FormTextField
              name="features.access.notes"
              control={control}
              label="Access Notes"
              placeholder="Any additional instructions about locks, remotes, or codes"
              multiline
              rows={2}
            />
          </Stack>
        </FormSection>
        

      </Box>
    </StepFormLayout>
  );
};

export default PropertyFeaturesStep; 
